import { useState } from "react";
import { Copy, Linkedin, Mail, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { trackEvent } from "@/utils/posthog";
import { shareUrl, suggestedMessage, type ShareChannel, type ShareWorkflow } from "@/utils/promptLibraryShare";

export function WorkflowShare({ workflow, placement, label, className }: {
  workflow?: ShareWorkflow;
  placement: "hero" | "workflow_card" | "after_copy";
  label?: string;
  className?: string;
}) {
  const [status, setStatus] = useState("");
  const [fallback, setFallback] = useState("");
  const [busy, setBusy] = useState(false);
  const track = (channel: ShareChannel, outcome: string) => trackEvent("Prompt Library Share Action", {
    channel, outcome, placement,
    workflow: workflow?.title || "library",
    workflow_slug: workflow?.slug || "library",
    source_page: "fortune-100-prompt-library",
  });

  const copy = async (channel: "copy_link" | "suggested_message") => {
    const url = shareUrl(channel, workflow);
    const value = channel === "suggested_message" ? suggestedMessage(url, workflow) : url;
    setStatus("");
    setFallback("");
    try {
      await navigator.clipboard.writeText(value);
      setStatus(channel === "copy_link" ? "Link copied." : "Message copied. Edit it before sending.");
      track(channel, "copied");
    } catch {
      setFallback(value);
      setStatus("Select and copy the text below.");
      track(channel, "manual_copy_needed");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      setFallback(shareUrl("native", workflow));
      setStatus("Your browser doesn’t offer a share menu. Copy this link instead.");
      track("native", "unavailable");
      return;
    }
    setBusy(true);
    setStatus("");
    setFallback("");
    track("native", "requested");
    try {
      await navigator.share({
        title: workflow?.title || "Fortune 100 AI Prompt Library",
        text: workflow?.purpose || "Practical AI workflows for everyday work.",
        url: shareUrl("native", workflow),
      });
      // Resolution is a handoff signal, not proof of publication or delivery.
      track("native", "api_resolved");
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        track("native", "cancelled");
      } else {
        track("native", "failed");
        setFallback(shareUrl("native", workflow));
        setStatus("The share menu couldn’t open. Copy this link instead.");
      }
    } finally { setBusy(false); }
  };

  const actionClass = "w-full justify-start gap-2";
  return (
    <Popover onOpenChange={() => { setStatus(""); setFallback(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={className || "gap-2 border-longhand-border bg-white text-longhand-ink hover:bg-longhand-accent-light"} aria-label={workflow ? `Share ${workflow.title}` : "Share this library"}>
          <Share2 className="h-4 w-4" aria-hidden="true" />{label || (workflow ? "Share workflow" : "Share this library")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)]">
        <p className="mb-3 text-sm font-semibold">{workflow?.title || "Share the library"}</p>
        <div className="space-y-1">
          <Button variant="ghost" className={actionClass} onClick={() => copy("copy_link")}><Copy className="h-4 w-4" aria-hidden="true" />Copy link</Button>
          <Button variant="ghost" className={actionClass} asChild><a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl("linkedin", workflow))}`} target="_blank" rel="noopener noreferrer" onClick={() => track("linkedin", "composer_requested")}><Linkedin className="h-4 w-4" aria-hidden="true" />LinkedIn</a></Button>
          <Button variant="ghost" className={actionClass} asChild><a href={`mailto:?subject=${encodeURIComponent(workflow?.title || "AI workflows for your team")}&body=${encodeURIComponent(suggestedMessage(shareUrl("email", workflow), workflow))}`} onClick={() => track("email", "composer_requested")}><Mail className="h-4 w-4" aria-hidden="true" />Email</a></Button>
          <Button variant="ghost" className={actionClass} disabled={busy} onClick={nativeShare}><Share2 className="h-4 w-4" aria-hidden="true" />More sharing options</Button>
          <Button variant="ghost" className={actionClass} onClick={() => copy("suggested_message")}>Copy suggested message</Button>
        </div>
        <p role="status" aria-live="polite" className="mt-2 text-sm text-muted-foreground">{status}</p>
        {fallback && <textarea aria-label="Text to copy manually" className="mt-2 h-28 w-full rounded border p-2 text-sm" readOnly value={fallback} onFocus={(event) => event.currentTarget.select()} />}
      </PopoverContent>
    </Popover>
  );
}
